import axios from "../../../axios/defaultAxios";
import { serializeQueryParams } from "../../../utils/utils";
import { slices } from "@/constants/sliceNames";
import { errorMessage, successMessage } from "@/store/actions/messages.actions";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { DEFAULT_DATE_RANGE, getStats, processExecutions } from "./utils";

const ENDPOINT = "/users/v2";

/****** Fetch all users *******/

export const requestUsersList = createAsyncThunk(
  `${slices.users}/requestUsersList`,
  async (filter, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(`${ENDPOINT}?${serializeQueryParams(filter)}`);
      if (response.status === 200) return response.data.data;
      throw new Error(response.data.error);
    } catch (error) {
      dispatch(errorMessage(error.message));
      return rejectWithValue(error.message);
    }
  }
);

export const requestUsersListStatus = {
  [requestUsersList.fulfilled]: (state, action) => {
    state.loading = false;
    state.items = action.payload.data;
    state.records_count = action.payload.records_count;
  },
  [requestUsersList.pending]: (state, action) => {
    state.loading = true;
  },
  [requestUsersList.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/****** Delete user *******/

export const requestDeleteUser = createAsyncThunk(
  `${slices.users}/requestDeleteUser`,
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.delete(`${ENDPOINT}/${id}`);
      dispatch(successMessage(response.data.message));
      return { id };
    } catch (error) {
      dispatch(errorMessage(error.response.data.error));
      return rejectWithValue(error.message);
    }
  }
);

export const requestDeleteUserStatus = {
  [requestDeleteUser.fulfilled]: (state, action) => {
    state.items = state.items.filter((item) => item._id !== action.payload.id);
    state.records_count -= 1;
    state.loading = false;
  },
  [requestDeleteUser.pending]: (state, action) => {
    state.loading = true;
  },
  [requestDeleteUser.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/****** Fetch single user by id *******/

export const requestSingleUser = createAsyncThunk(
  `${slices.users}/requestSingleUser`,
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(`${ENDPOINT}/${id}`);
      return response.data.data;
    } catch (error) {
      dispatch(errorMessage(error.response.data.error));
      return rejectWithValue(error.message);
    }
  }
);

export const requestSingleUserStatus = {
  [requestSingleUser.fulfilled]: (state, action) => {
    state.item = action.payload;
    state.loading = false;
  },
  [requestSingleUser.pending]: (state, action) => {
    state.loading = true;
  },
  [requestSingleUser.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/****** Update user *******/

export const requestUpdateUser = createAsyncThunk(
  `${slices.users}/requestUpdateUser`,
  async ({ id, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.put(`${ENDPOINT}/${id}`, data);
      dispatch(successMessage(response.data.message));
      return data;
    } catch (error) {
      dispatch(errorMessage(error.response.data.error));
      return rejectWithValue(error.message);
    }
  }
);

export const requestUpdateUserStatus = {
  [requestUpdateUser.fulfilled]: (state, action) => {
    state.loading = false;
  },
  [requestUpdateUser.pending]: (state, action) => {
    state.loading = true;
  },
  [requestUpdateUser.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload;
  },
};

/****** Fetch user execution stats *******/

export const requestUserExecutionsStats = createAsyncThunk(
  `${slices.users}/requestUserExecutionsStats`,
  async ({ id, type = "All", dateRange = DEFAULT_DATE_RANGE }, { rejectWithValue, dispatch }) => {
    try {
      const params = {
        id,
      };
      const execs = await getStats(type, params, dateRange, "userExecustions");
      const { resources, ...userExecs } = processExecutions(execs);
      const fullRes = { userExecs, resources };
      return fullRes;
    } catch (error) {
      dispatch(errorMessage(error.response.data.error));
      return rejectWithValue(error.message);
    }
  }
);

export const requestUserExecutionsStatsStatus = {
  [requestUserExecutionsStats.fulfilled]: (state, action) => {
    state.executionLoading = false;
    state.statistics.resources = action.payload.resources;
    state.statistics.userExecs = action.payload.userExecs;
  },
  [requestUserExecutionsStats.pending]: (state, action) => {
    state.executionLoading = true;
  },
  [requestUserExecutionsStats.rejected]: (state, action) => {
    state.executionLoading = false;
    state.error = action.payload;
  },
};

/****** Fetch user dataset stats *******/

export const requestUserStatsDataset = createAsyncThunk(
  `${slices.organizations}/requestUserStatsDataset`,
  async ({ id, type = "All", dateRange = DEFAULT_DATE_RANGE }, { rejectWithValue, dispatch }) => {
    try {
      const params = {
        id,
      };
      const stats = await getStats(type, params, dateRange, "userStatistics");
      const newData = stats.reduce((result, value, key) => {
        result[value.ID] = value;
        return result;
      }, {});
      return { dataStats: newData };
    } catch (error) {
      dispatch(errorMessage(error.response.data.error));
      return rejectWithValue(error.message);
    }
  }
);

export const requestUserStatsDatasetStatus = {
  [requestUserStatsDataset.fulfilled]: (state, action) => {
    state.datasetLoading = false;
    state.statistics.dataStats = action.payload.dataStats;
  },
  [requestUserStatsDataset.pending]: (state, action) => {
    state.datasetLoading = true;
  },
  [requestUserStatsDataset.rejected]: (state, action) => {
    state.datasetLoading = false;
    state.error = action.payload;
  },
};
